import React, {useEffect, useState, memo} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  MenuItem,
  Select,
  Theme,
  Typography,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  DraggableDialog,
  ProgressDialog,
  PromptDialog,
  DenseDivider,
  IconToolBar,
  SlimScrollbar,
  ToolBarButton,
  EnhancedIconButton,
  DropDownDialog,
  TextDialog,
  CheckboxWithLabel
} from '../components';
import { fs, getTemporaryFile, registerEvent } from '../electron-modules';
import { FolderWithLabel } from '../components';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSelector } from 'react-redux';
import {
  addAsset,
  Asset,
  AssetType,
  deleteAsset,
  getPointCloudAssets,
  selectAllAssets
} from '../redux/assets-slice';
import { PythonExecutable } from '../executable';
import path from 'path';
import { asyncTimeout, useAssetTools } from '../utilities';
import { toast } from '../app';
import { useTranslation } from "react-i18next";
import { classifications } from '../classifications';
import { textToColor } from '../viewer/labelling';
import { createFolder } from '../redux/folders-slice';
import { ClippingBox, ClippingGroup, OperationType } from '../viewer/markup';
import {
  useDialog,
  useUniqueProjectID,
  useViewer,
  useActiveTool
} from '../hooks';
import { useAppDispatch } from '../redux/store';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      margin: theme.spacing(2),
      marginTop: theme.spacing(0),
      padding: theme.spacing(1),
      userSelect: "none"
    },
    cardHeader: {
      padding: theme.spacing(1)
    },
    cardContent: {
      padding: `${theme.spacing(2)} !important`
    },
    cardButtons: {
      marginTop: 0,
      alignSelf: "center"
    },
    headerTitle: {
      fontWeight: "bold"
    },
    clippingBoxParent: {
      display: "flex",
      justifyContent: "space-between"
    },
    clippingBoxButtons: {
      color: "green"
    },
    folderList: {
      padding: "0px",
      overflow: "auto",
      flex: 1,
      alignItems: "center",
      justifyContent: "center"
    },
    operationList: {
      flex: 1,
      overflow: "hidden",
      overflowY: "auto"
    },
    mixedDataWarning: {
      padding: theme.spacing(1.0),
      color: theme.palette.warning.main,
      textAlign: "center"
    },
    boxOrientation: {
      display: "flex",
      justifyContent: "space-between",
      paddingBottom: theme.spacing(1.5),
      fontSize: "0.85em",
      "&:last-child": {
        paddingBottom: theme.spacing(0)
      }
    },
    dropdownRoot: {
      minWidth: "40%"
    },
    dropdownSelect: {
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      fontSize: "0.85em"
    }
  })
);

interface CategoryExpandProps {
  expanded: boolean;
  setExpanded: React.Dispatch<boolean>;
}

const CategoryExpand = (props: CategoryExpandProps) => {
  const {setExpanded, expanded} = props;

  const {t} = useTranslation();

  return (<EnhancedIconButton
    title={expanded? t('general.collapse') : t('general.expand')}
    onClick={(event) => {
      event.stopPropagation();
      setExpanded(!expanded);
    }}
  >
    {expanded && <ExpandLessIcon fontSize="small"/>}
    {!expanded && <ExpandMoreIcon fontSize="small"/>}
  </EnhancedIconButton>);
};

interface PointMarkupBoxProps {
  index: number;
  clippingBox: ClippingBox
}

const PointMarkupBox = (props: PointMarkupBoxProps) => {
  const {clippingBox, index} = props;

  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const [deletePrompt, setDeletePrompt] = useState(false);

  const handleDelete = () => {
    setDeletePrompt(false);
    viewer.pointMarkupDeleteBox(clippingBox.id);
  };

  const onMouseOver = () => {
    viewer.setPointMarkupHover(clippingBox.id, true);
  };

  const onMouseLeave = () => {
    viewer.setPointMarkupHover(clippingBox.id, false);
  };

  return (
    <ListItem
      disableGutters
      className={classes.clippingBoxParent}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
    >
      <Typography
        variant="body2"
        className={classes.headerTitle}
      >
        {t('point-markup.bounding_box_index', {
          index: index+1
        })}
      </Typography>

      <div className={classes.clippingBoxButtons}>
        {/* Zoom to extent */}
        <EnhancedIconButton
          title={t('point-markup.zoom_to_bounding_box')}
          onClick={() => {
            viewer.pointMarkupZoom(clippingBox.id);
          }}
        >
          <ZoomInIcon fontSize="small"/>
        </EnhancedIconButton>

        {/* Reset rotation */}
        <EnhancedIconButton
          title={t('point-markup.reset_rotation')}
          onClick={() => {
            viewer.resetPointMarkupRotation(clippingBox.id);
          }}
        >
          <RefreshIcon fontSize="small"/>
        </EnhancedIconButton>

        {/* Delete bounding box */}
        <EnhancedIconButton
          title={t('buttons.delete')}
          onClick={() => {
            setDeletePrompt(true);
          }}
        >
          <DeleteIcon fontSize="small"/>
        </EnhancedIconButton>
      </div>

      {/* Delete Dialog */}
      <PromptDialog
        open={deletePrompt}
        onSubmit={handleDelete}
        onCancel={() => {
          setDeletePrompt(false);
        }}
        title={t('point-markup.delete_bounding_box')}
        prompt={t('point-markup.do_you_want_to_delete_this_bounding_box')}
        button={t('buttons.delete')}
      />

    </ListItem>
  );
};

interface PointMarkupGroupProps {
  operation: ClippingGroup
}

const PointMarkupGroup = (props: PointMarkupGroupProps) => {
  const {operation} = props;

  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const [expanded, setExpanded] = useState(false);
  const [editPrompt, setEditPrompt] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState(false);
  const [newClassification, setNewClassification] = useState(0);

  const isDelete = operation.type === OperationType.Delete;
  const isClassify = operation.type === OperationType.Classify;
  const isSplit = operation.type === OperationType.Split;

  const color = textToColor(operation.name);
  const newBoxDisabled = viewer?.pointMarkupActive();
  const operationBoxes = operation.clippingBoxes;

  const defaultIgnoreValue = isSplit ? -1 : 2;
  const [ignoredClassification, setIgnoredClassification]
    = useState(defaultIgnoreValue);

  const handleDelete = () => {
    setDeletePrompt(false);
    viewer.pointMarkupDeleteGroup(operation.id);
  };

  const handleRename = (name) => {
    if (viewer.pointMarkupNameExists(name)) {
      toast.error(t('toast.filename_already_exists'));
      return;
    }

    setEditPrompt(false);
    viewer.updatePointMarkupName(operation.id, name);
  };

  const addThenExpand = () => {
    viewer.setPointMarkupActive(operation.id);
    setExpanded(true);
  };

  useEffect(() => {
    viewer.setPointMarkupNewClassification(operation.id, newClassification);
  }, [viewer, newClassification]);

  useEffect(() => {
    viewer.setPointMarkupIgnoredClassification(operation.id, ignoredClassification);
  }, [viewer, ignoredClassification]);

  let classOptions = [];
  for (let i=0; i<32; i++) {
    const {id, name} = classifications[i];
    const displayName = `${name} (${id})`;
    classOptions.push(<MenuItem
      key={id}
      value={id}
    >{displayName}</MenuItem>);
  };

  return (
    <React.Fragment>
      <Card
        className={classes.card}
      >
        {/* Header */}
        <CardHeader
          className={classes.cardHeader}
          classes={{
            action: classes.cardButtons
          }}
          action={
            <React.Fragment>
              {/* Expand/collapse areas */}
              <CategoryExpand
                setExpanded={setExpanded}
                expanded={expanded}
              />

              {/* Edit output name */}
              <EnhancedIconButton
                title={t('labels.edit-name')}
                onClick={() => {
                  setEditPrompt(true);
                }}
              >
                <EditIcon fontSize="small"/>
              </EnhancedIconButton>

              {/* Add new bounding box */}
              <EnhancedIconButton
                title={t('point-markup.add_new_bounding_box')}
                onClick={addThenExpand}
                disabled={newBoxDisabled}
              >
                <AddToPhotosIcon fontSize="small"/>
              </EnhancedIconButton>

              {/* Delete operation box */}
              <EnhancedIconButton
                title={t('buttons.delete')}
                onClick={() => {
                  setDeletePrompt(true);
                }}
              >
                <DeleteIcon fontSize="small"/>
              </EnhancedIconButton>
            </React.Fragment>
          }
          disableTypography={true}
          title={
            <Typography
              variant="body2"
              style={{color}}
              className={classes.headerTitle}
            >
              {operation.name}
            </Typography>
          }
          subheader={
            <Typography variant="caption">
              {t('point-markup.bounding_boxes', {
                count: operationBoxes.length
              })}
            </Typography>
          }
        />

        {expanded && (<CardContent className={classes.cardContent}>
          {/** Ignore Classification */}
          {(isClassify || isDelete) && (<React.Fragment>
            <div className={classes.boxOrientation}>
              <b>{t('point-markup.ignore_classification')}</b>
              <Select
                className={classes.dropdownRoot}
                classes={{select: classes.dropdownSelect}}
                value={ignoredClassification}
                onChange={(event: any) => {
                  setIgnoredClassification(event.target.value);
                }}
              >
                <MenuItem value={-1}>{t('point-markup.use_all_classes')}</MenuItem>
                {classOptions}
              </Select>
            </div>
          </React.Fragment>)}

          {/** New classification */}
          {isClassify && (<React.Fragment>
            <div className={classes.boxOrientation}>
              <b>{t('point-markup.new_classification')}</b>
              <Select
                className={classes.dropdownRoot}
                classes={{select: classes.dropdownSelect}}
                value={newClassification}
                onChange={(event: any) => {
                  setNewClassification(event.target.value);
                }}
              >
                {classOptions}
              </Select>
            </div>
          </React.Fragment>)}

          {(operationBoxes.length > 0) && (<React.Fragment>
            <DenseDivider variant="middle" spacing={5}/>

            {/** Bounding boxes */}
            <List className={classes.folderList}>
              {operationBoxes.map((clippingBox, index) =>
                <PointMarkupBox
                  key={index}
                  index={index}
                  clippingBox={clippingBox}
                />
              )}
            </List>
          </React.Fragment>)}

        </CardContent>)}
      </Card>

      {/* Delete Dialog */}
      <PromptDialog
        open={deletePrompt}
        onSubmit={handleDelete}
        onCancel={() => {
          setDeletePrompt(false);
        }}
        title={t('point-markup.delete_bounding_boxes')}
        prompt={t('point-markup.do_you_want_to_delete_this_operation_including_all_bounding_boxes')}
        button={t('buttons.delete')}
      />

      {/* Output Name Dialog */}
      <TextDialog
        clear
        open={editPrompt}
        onCancel={() => setEditPrompt(false)}
        onSubmit={handleRename}
        title={t('point-markup.edit_output_file')}
        prompt={t('point-markup.enter_new_filename')}
        placeholder={operation.name}
        label={t('point-markup.output_file')}
      />
    </React.Fragment>
  );
};

interface DataExportSplitProps {
  open: boolean;
  setOpen: React.Dispatch<boolean>;
  setProgress: React.Dispatch<number>;
  markupGroups: ClippingGroup[]
  visibleClouds: Asset[];
}

const DataExportSplit = (props: DataExportSplitProps) => {
  const {open, setOpen, setProgress, markupGroups, visibleClouds} = props;

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const {addAssetPaths} = useAssetTools();

  const [exportPoints, setExportPoints] = useState(true);
  const [exportImages, setExportImages] = useState(true);
  const [outputPath, setOutputPath] = useState(null);

  const handleClose = () => {
    setOpen(false);
    setExportPoints(true);
    setExportImages(true);
    setOutputPath(null);
    setProgress(0.0);
  };

  const handleSubmit = () => {
    if (visibleClouds.length === 0) {
      toast.warning(t("toast.no-point-clouds"));
      return;
    }

    const operationData = markupGroups.map(operation => {
      const operationBoxes = operation.clippingBoxes;
      const attributes = operationBoxes.map(x => x.attributes);

      return {
        name: operation.name,
        data: attributes
      };
    });

    const cameras = viewer.getAllCameras();
    const cameraData = cameras.map(camera => {
      const position = camera.position.value
        .toArray();

      const rotation = camera.rotation.degrees
        .toArray()
        .slice(0,3);

      return {
        name: camera.name,
        path: camera.path,
        position,
        rotation
      };
    });

    const jsonData = {
      operationData,
      outputPath,
      inputPaths: exportPoints ? inputPaths : [],
      imageData: exportImages ? cameraData : [],
    };

    // Write file to temp path
    const text = JSON.stringify(jsonData, null, 4);
    const tempTextPath = getTemporaryFile("txt");

    try {
      fs.writeFileSync(tempTextPath, text);
    } catch(err) {
      console.log(err);
      toast.error(t("toast.markup-failed"));

      return;
    }

    toast.success(t("toast.markup-submit"));

    let outputPaths = null;
    let processTimeout = 500;

    const exe = new PythonExecutable();
    let commands = [
      "-p", "apply_data_splitting",
      "--input_json", tempTextPath
    ];

    exe.run({
      commands,
      saveLogs: true,
      onLine: jsonData => {
        if (jsonData.progress) {
          setProgress(jsonData.progress);
        } else if (jsonData.output_paths) {
          outputPaths = jsonData.output_paths;
        }
      },
      onClose: async () => {
        if (!outputPaths) {
          toast.error(t("toast.markup-failed"));
          handleClose();
          return;
        }

        await asyncTimeout(processTimeout);
        viewer.clearPointMarkup();
        toast.success(t("toast.markup-import"));

        for (let outputPath of outputPaths) {
          const folder = await dispatch(createFolder(outputPath.name));
          await addAssetPaths(outputPath.paths, folder.id);
        }

        handleClose();
      }
    });
  };

  let canSubmit = false;
  let pathOverlap = false;

  const inputPaths = visibleClouds.map(x => x.path);
  const inputFoldersPoints = inputPaths.map(x => path.dirname(x));

  if (viewer) {
    const cameras = viewer.getAllCameras();
    const inputFolderCameras = viewer.getCameraFolders(cameras);

    const inputFolders = new Set([
      ...inputFoldersPoints,
      ...inputFolderCameras
    ]);

    pathOverlap = inputFolders.has(outputPath);

    canSubmit = !pathOverlap
      && (outputPath !== null)
      && (exportImages || exportPoints);
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={"xs"}
    >
      <DialogTitle>{t('point-markup.point-markup-export')}</DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>

          {pathOverlap && (<Grid item xs={12} className={classes.mixedDataWarning}>
            <Typography variant="caption" align="center">
              {t('point-markup.output-folder-must-be-different')}
            </Typography>
          </Grid>)}

          {/* Include images in export */}
          <Grid item xs={12}>
            <CheckboxWithLabel
              title={t('point-markup.export_cameras_images')}
              value={exportImages}
              onChange={setExportImages}
            />
          </Grid>

          {/* Include points in export */}
          <Grid item xs={12}>
            <CheckboxWithLabel
              title={t('point-markup.export_point_clouds')}
              value={exportPoints}
              onChange={setExportPoints}
            />
          </Grid>

          {/* Output folder */}
          <Grid item xs={12}>
            <FolderWithLabel
              title={t('point-markup.output-folder')}
              label={t('point-markup.output_folder_where_new_point_clouds')}
              value={outputPath}
              onChange={setOutputPath}
            />
          </Grid>

        </Grid>

      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('buttons.cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit} color="primary">
          {t('buttons.submit')}
        </Button>
      </DialogActions>

    </Dialog>
  );
};

interface DataExportMarkupProps {
  open: boolean;
  setOpen: React.Dispatch<boolean>;
  setProgress: React.Dispatch<number>;
  markupGroups: ClippingGroup[]
  visibleClouds: Asset[];
}

const DataExportMarkup = (props: DataExportMarkupProps) => {
  const {open, setOpen, setProgress, markupGroups, visibleClouds} = props;

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const [overwriteFile, setOverwriteFile] = useState(false);
  const [outputPath, setOutputPath] = useState(null);

  const handleClose = () => {
    setOpen(false);
    setOverwriteFile(false);
    setOutputPath(null);
    setProgress(0.0);
  };

  const handleSubmit = () => {
    if (visibleClouds.length === 0) {
      toast.warning(t("toast.no-point-clouds"));
      return;
    }

    let boundingBoxes = markupGroups.map(operation => {
      const operationBoxes = operation.clippingBoxes;
      const attributes = operationBoxes.map(x => x.attributes);
      return attributes;
    }).flat();

    const jsonData = {
      inputPaths,
      outputPath,
      boundingBoxes,
      overwriteFile
    };

    // Write file to temp path
    const text = JSON.stringify(jsonData, null, 4);
    const tempTextPath = getTemporaryFile("txt");

    try {
      fs.writeFileSync(tempTextPath, text);
    } catch(err) {
      console.log(err);
      toast.error(t("toast.markup-failed"));

      return;
    }

    toast.success(t("toast.markup-submit"));

    let newCloudPaths = null;
    let processTimeout = 500;

    const exe = new PythonExecutable();
    let commands = [
      "-p", "apply_point_markup",
      "--input_json", tempTextPath
    ];

    exe.run({
      commands,
      saveLogs: true,
      onLine: jsonData => {
        if (jsonData.progress) {
          setProgress(jsonData.progress);
        } else if (jsonData.output_paths) {
          newCloudPaths = jsonData.output_paths;
        }
      },
      onClose: async () => {
        if (!newCloudPaths) {
          toast.error(t("toast.markup-failed"));
          handleClose();
          return;
        }

        await asyncTimeout(processTimeout);
        viewer.clearPointMarkup();
        toast.success(t("toast.markup-import"));

        visibleClouds.forEach((asset, index) => {

          const assetPath = newCloudPaths[index];

          // Add new pointcloud
          dispatch(addAsset({
            folderID: asset.folderID,
            name: path.basename(assetPath),
            path: assetPath,
            type: asset.type
          }));

          // Remove old pointcloud
          dispatch(deleteAsset(asset.id));
        });

        handleClose();
      }
    });
  };

  const inputPaths = visibleClouds.map(x => x.path);
  const inputFolders = inputPaths.map(x => path.dirname(x));

  let canSubmit = true;

  let pathOverlap = inputFolders.includes(outputPath);
  if (pathOverlap) {
    canSubmit = false;
  }

  if (!overwriteFile && (outputPath === null)) {
    canSubmit = false;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={"xs"}
    >
      <DialogTitle>{t('point-markup.point-markup-export')}</DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>

          {pathOverlap && (<Grid item xs={12} className={classes.mixedDataWarning}>
            <Typography variant="caption" align="center">
              {t('point-markup.output-folder-must-be-different')}
            </Typography>
          </Grid>)}

          {/* Setting layer default */}
          <Grid item xs={12}>
            <CheckboxWithLabel
              title={t('point-markup.overwrite-existing-las-files')}
              value={overwriteFile}
              onChange={(value) => {
                setOverwriteFile(value);
                setOutputPath(null);
              }}
            />
          </Grid>

          {/* Output folder */}
          {!overwriteFile && (<Grid item xs={12}>
            <FolderWithLabel
              title={t('point-markup.output-folder')}
              label={t('point-markup.output-folder-desc')}
              value={outputPath}
              onChange={setOutputPath}
            />
          </Grid>)}

        </Grid>

      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('buttons.cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit} color="primary">
          {t('buttons.submit')}
        </Button>
      </DialogActions>

    </Dialog>
  );
};

interface PointMarkupProps {
  markupGroups: ClippingGroup[]
}

export const PointMarkup = memo((props: PointMarkupProps) => {
  const {markupGroups} = props;

  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const pointMarkupDialog = useDialog();
  const uniqueProjectID = useUniqueProjectID();
  const {activeToolOpen, setActiveToolOpen} = useActiveTool();

  const [toolType, setToolType] = useState(null);
  const [closePrompt, setClosePrompt] = useState(false);
  const [exportPromptMarkup, setExportPromptMarkup] = useState(false);
  const [exportPromptSplit, setExportPromptSplit] = useState(false);
  const [newOperationPrompt, setNewOperationPrompt] = useState(false);
  const [newOutputPrompt, setNewOutputPrompt] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);

  const assets = useSelector(selectAllAssets);
  const pointClouds = getPointCloudAssets(assets);
  const LASfiles = pointClouds.filter(x => x.type === AssetType.LAS);
  const visibleClouds = LASfiles.filter(x => x.visible);

  const checkClose = () => {
    if (isModified) {
      setClosePrompt(true);
    } else {
      pointMarkupDialog.handleClose();
    }
  };

  const addNewOperation = (operationType) => {
    setNewOperationPrompt(false);

    if (operationType === OperationType.Delete) {
      viewer.addPointMarkupOperation(operationType, t('point-markup.delete_point_data'));
    } else if (operationType === OperationType.Classify) {
      viewer.addPointMarkupOperation(operationType, t('point-markup.classify_point_data'));
    }
  };

  const addNewOutputFolder = (name) => {
    if (viewer.pointMarkupNameExists(name)) {
      toast.error(t('toast.filename_already_exists'));
      return;
    }

    setNewOutputPrompt(false);

    const operationType = OperationType.Split;
    viewer.addPointMarkupOperation(operationType, name);
  };

  useEffect(() => {
    /** Keep track if a tool is currently being used */
    setActiveToolOpen(pointMarkupDialog.open);
  }, [pointMarkupDialog.open]);

  useEffect(() => {
    /** Close dialog when new project is loaded */
    pointMarkupDialog.handleClose();
  }, [uniqueProjectID]);

  useEffect(() => {
    registerEvent("open-point-markup", () => {
      if (activeToolOpen) return toast.warning(t('toast.one_tool_only'));
      pointMarkupDialog.handleOpen("markup");
    });

    registerEvent("open-data-splitting", () => {
      if (activeToolOpen) return toast.warning(t('toast.one_tool_only'));
      pointMarkupDialog.handleOpen("split");
    });
  }, [activeToolOpen]);

  useEffect(() => {
    if (!viewer) return;
    viewer.pointMarkupToggle(pointMarkupDialog.open);
  }, [viewer, pointMarkupDialog.open]);

  useEffect(() => {
    setToolType(pointMarkupDialog.data);
  }, [pointMarkupDialog.data]);

  const isPointMarkup = toolType === "markup";
  const isFolderSplit = toolType === "split";
  const isModified = markupGroups.length > 0;
  const mixedData = pointClouds.length !== LASfiles.length;

  const dialogTitle = isPointMarkup
    ? t('point-markup.point-cloud-markup')
    : t('point-markup.data-splitting');

  return (
    <React.Fragment>
      <DraggableDialog
        initialHeight={500}
        open={pointMarkupDialog.open}
        title={dialogTitle}
        onClose={checkClose}
      >

        {/* Label toolbar icons */}
        <IconToolBar margin={1}>
          {/* Add new operation */}
          <ToolBarButton
            title={t('point-markup.add_operation')}
            icon={"fa-plus-circle"}
            visible={isPointMarkup}
            onClick={() => {
              setNewOperationPrompt(true);
            }}
            success
          />

          {/* Add new output file */}
          <ToolBarButton
            title={t('point-markup.add_file_output')}
            icon={"fa-plus-circle"}
            visible={isFolderSplit}
            onClick={() => {
              setNewOutputPrompt(true);
            }}
            success
          />

          {/* Apply changes to las file */}
          <ToolBarButton
            title={t('buttons.save-changes')}
            icon={"fa-download"}
            disabled={!isModified}
            onClick={() => {
              if (isPointMarkup) {
                setExportPromptMarkup(true);
              } else {
                setExportPromptSplit(true);
              }
            }}
          />
        </IconToolBar>

        <DenseDivider variant="middle" spacing={5}/>

        {mixedData && (<Box className={classes.mixedDataWarning}>
          <Typography variant="caption" align="center">
            {t('point-markup.will-only-to-las-files')}
          </Typography>
        </Box>)}

        <DialogContent>
          <SlimScrollbar>
            <List dense className={classes.operationList}>
              {markupGroups.map(operation =>
                <PointMarkupGroup
                  key={operation.id}
                  operation={operation}
                />
              )}
            </List>
          </SlimScrollbar>
        </DialogContent>

      </DraggableDialog>

      {/* Close modal */}
      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          pointMarkupDialog.handleClose();
          setClosePrompt(false);
        }}
        title={t('point-markup.close-window')}
        prompt={t('point-markup.want-to-close')}
        button={t('point-markup.close')}
      />

      {/* Add new operation */}
      <DropDownDialog
        open={newOperationPrompt}
        onCancel={() => {
          setNewOperationPrompt(false);
        }}
        onSubmit={addNewOperation}
        title={t('point-markup.add_new_operation')}
        prompt={t('point-markup.select_operation_type')}
        defaultValue={OperationType.Delete}
        options={[
          [OperationType.Delete, t('point-markup.delete_point_data')],
          [OperationType.Classify, t('point-markup.classify_point_data')]
        ]}
      />

      {/* Add output folder */}
      <TextDialog
        clear
        open={newOutputPrompt}
        onCancel={() => {
          setNewOutputPrompt(false);
        }}
        onSubmit={addNewOutputFolder}
        title={t('point-markup.create_new_output')}
        prompt={t('point-markup.enter_new_file_name')}
        label={t('point-markup.output_file_name')}
      />

      {/* Conversion percent dialog */}
      <ProgressDialog
        open={conversionProgress > 0}
        text={t('point-markup.applying-point-cloud-markup')}
        percent={conversionProgress}
      />

      {/* Point markup export */}
      {exportPromptMarkup && (<DataExportMarkup
        open={exportPromptMarkup}
        setOpen={setExportPromptMarkup}
        setProgress={setConversionProgress}
        markupGroups={markupGroups}
        visibleClouds={visibleClouds}
      />)}

      {/* Data splitting export */}
      {exportPromptSplit && (<DataExportSplit
        open={exportPromptSplit}
        setOpen={setExportPromptSplit}
        setProgress={setConversionProgress}
        markupGroups={markupGroups}
        visibleClouds={visibleClouds}
      />)}

    </React.Fragment>
  );
});
